import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../shared/TailwindComponents/Button';
import SearchInput from '../../shared/TailwindComponents/SearchInput';
import { DraftType } from './DraftTemplateManager';

interface DraftTypeSelectorProps {
  allDraftTypes: DraftType[];
  selectedDraftTypeIds: number[];
  onChange: (selectedIds: number[]) => void;
}

const DraftTypeSelector: React.FC<DraftTypeSelectorProps> = ({ allDraftTypes, selectedDraftTypeIds, onChange }) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedParents, setExpandedParents] = useState<number[]>([]);

  const childDraftTypes = useMemo(() => allDraftTypes.filter(dt => dt.ParentDraftID !== null), [allDraftTypes]);
  const availableDraftTypes = useMemo(
    () => childDraftTypes.filter(dt => !selectedDraftTypeIds.includes(dt.DraftID)),
    [childDraftTypes, selectedDraftTypeIds]
  );
  const selectedDraftTypes = useMemo(
    () => childDraftTypes.filter(dt => selectedDraftTypeIds.includes(dt.DraftID)),
    [childDraftTypes, selectedDraftTypeIds]
  );
  const draftTypeMap = useMemo(() => {
    return allDraftTypes.reduce((acc, dt) => {
      acc[dt.DraftID] = dt;
      return acc;
    }, {} as { [key: number]: DraftType });
  }, [allDraftTypes]);

  const activeSelectedCount = useMemo(
    () => selectedDraftTypes.filter(dt => dt.IsActive ?? false).length,
    [selectedDraftTypes]
  );
  const inactiveAvailableCount = useMemo(
    () => availableDraftTypes.filter(dt => !(dt.IsActive ?? false)).length,
    [availableDraftTypes]
  );

  const filteredAvailableDraftTypes = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return availableDraftTypes.filter(dt =>
      dt.Name.toLowerCase().includes(query) || dt.Description.toLowerCase().includes(query)
    );
  }, [availableDraftTypes, searchQuery]);

  const groupedFilteredAvailable = useMemo(() => {
    return filteredAvailableDraftTypes.reduce((acc, dt) => {
      const parentId = dt.ParentDraftID!;
      if (!acc[parentId]) acc[parentId] = [];
      acc[parentId].push(dt);
      return acc;
    }, {} as { [key: number]: DraftType[] });
  }, [filteredAvailableDraftTypes]);

  const handleAddDraftType = (draftTypeId: number) => {
    onChange([...selectedDraftTypeIds, draftTypeId]);
  };

  const handleRemoveDraftType = (draftTypeId: number) => {
    onChange(selectedDraftTypeIds.filter(id => id !== draftTypeId));
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-6">
        <div>
          <h3 className="text-lg font-medium text-gray-700 dark:text-gray-200 mb-2">
            {t('Selected Document Types')} 
            <span className='ms-2 text-xs'>({activeSelectedCount})</span>
          </h3>
          {selectedDraftTypes.length > 0 ? (
            <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 p-4 bg-gray-100 dark:bg-gray-800 rounded border border-gray-300">
              {selectedDraftTypes.map(dt => (
                <li key={dt.DraftID} className="flex items-center justify-between p-2 bg-white dark:bg-info-800 rounded">
                  <div className='flex flex-col'>
                    <span className="text-sm text-gray-900 dark:text-white">{dt.Name}</span>
                    <span className="text-xs text-gray-800 dark:text-gray-100">{dt.Description}</span>
                  </div>
                  <Button onClick={() => handleRemoveDraftType(dt.DraftID)} variant="destructive" size="small">
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('No selected draft types')}</p>
          )}
        </div>
        <div>
          <h3 className="text-lg font-medium text-gray-700 dark:text-gray-200 mb-2">
            {t('Available Document Types')}
          <span className='ms-2 text-xs'>({inactiveAvailableCount})</span>
          </h3>
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={t('Search draft types...')}
            wrapperClassName="w-full mb-4"
          />
          <div className='grid grid-cols-1 gap-4 md:grid-cols-2 '>
          {Object.keys(groupedFilteredAvailable).length > 0 ? (
            Object.keys(groupedFilteredAvailable).map(parentIdStr => {
              const parentId = parseInt(parentIdStr);
              const parent = draftTypeMap[parentId];
              const childTypes = groupedFilteredAvailable[parentId];
              const isExpanded = expandedParents.includes(parentId);
              const displayedChildTypes = isExpanded ? childTypes : childTypes.slice(0, 3);
              return (
                <div key={parentId} className="mb-4 p-4 bg-gray-100 dark:bg-gray-800 rounded border border-gray-300">
                  <h4 className="text-md font-semibold text-gray-800 dark:text-gray-100">{parent.Name}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{parent.Description}</p>
                  <ul className="mt-2 space-y-2">
                    {displayedChildTypes.map(dt => (
                      <li key={dt.DraftID} className="flex items-center justify-between p-2 rounded bg-white dark:bg-gray-900">
                        <div>
                          <span className="text-sm text-gray-800 dark:text-gray-100">{dt.Name}</span>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{dt.Description}</p>
                        </div>
                        <Button onClick={() => handleAddDraftType(dt.DraftID)} variant="primary" size="small" className="text-info-500">
                          Add
                        </Button>
                      </li>
                    ))}
                  </ul>
                  {childTypes.length > 3 && (
                    <Button
                      onClick={() => setExpandedParents(prev => isExpanded ? prev.filter(id => id !== parentId) : [...prev, parentId])}
                      variant="outline"
                      size="small"
                      className="mt-2 text-info-500"
                    >
                      {isExpanded ? t('Show Less') : t('Show More')}
                    </Button>
                  )}
                </div>
              );
            })
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('No available document types')}</p>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DraftTypeSelector;